'use client'
import React, { useState, useEffect } from 'react'
import Image from 'next/image'

import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Link from '@mui/material/Link'
import FacebookIcon from '@mui/icons-material/Facebook'
import XIcon from '@mui/icons-material/X'
import { useCustomTheme } from '@/context/ThemeProvider'

import { APP_FACEBOOK_PROFILE, APP_NAMESPACE, APP_TWITTER_PROFILE, TITLE } from '@/constants/AppConfig'

const Footer = () => {
  // Use state to track if the footer content is loaded
  const [isLoaded, setIsLoaded] = useState(false)

  // Set loaded state after component mounts
  useEffect(() => {
    setIsLoaded(true)
  }, [])

  return (
    <Box
      component="footer"
      sx={{
        backgroundColor: useCustomTheme().darkPalette.primary.main,
        color: useCustomTheme().darkPalette.primary.contrastText,
        marginTop: 'auto',
        width: '100%',
        visibility: isLoaded ? 'visible' : 'hidden',
        py: { xs: 2, md: 6 }, // Further reduced mobile padding
        px: { xs: 2, sm: 4, md: 6, lg: 8 },
      }}
    >
      {/* Main Footer Content */}
      <Box
        sx={{
          maxWidth: '1200px',
          mx: 'auto',
        }}
      >
        {/* Mobile Layout - Ultra Compact */}
        <Box
          sx={{
            display: { xs: 'block', lg: 'none' },
          }}
        >
          {/* Logo - Mobile */}
          <Box sx={{ textAlign: 'center', mb: 2 }}>
            <Image
              src={`/${APP_NAMESPACE}/images/logo.png`}
              alt="Logo"
              width={180} // Even smaller on mobile
              height={50}
              loading="lazy"
              title={TITLE}
              style={{ height: 'auto', width: 'auto' }}
              onLoad={() => setIsLoaded(true)}
            />
          </Box>

          {/* All Links in One Compact Row - Mobile */}
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 1.5,
              justifyContent: 'center',
              mb: 2,
              fontSize: '0.7rem',
            }}
          >
            <Link href="/about" color="inherit" sx={{ textDecoration: 'none' }} title={`${TITLE} : About Us`}>
              About
            </Link>
            <Link href="/contact" color="inherit" sx={{ textDecoration: 'none' }} title={`${TITLE} : Contact Us`}>
              Contact
            </Link>
            <Link
              href="/privacy-policy"
              color="inherit"
              sx={{ textDecoration: 'none' }}
              title={`${TITLE} : Privacy Policy`}
            >
              Privacy
            </Link>
            <Link
              href="/terms-of-service"
              color="inherit"
              sx={{ textDecoration: 'none' }}
              title={`${TITLE} : Terms of Service`}
            >
              Terms
            </Link>
            <Link href="/disclaimer" color="inherit" sx={{ textDecoration: 'none' }} title={`${TITLE} : Disclaimer`}>
              Disclaimer
            </Link>
            <Link
              href="/correction-policy"
              color="inherit"
              sx={{ textDecoration: 'none' }}
              title={`${TITLE} : Correction Policy`}
            >
              Corrections
            </Link>
            <Link
              href="/fact-checking-policy"
              color="inherit"
              sx={{ textDecoration: 'none' }}
              title={`${TITLE} : Fact Checking Policy`}
            >
              Ethics
            </Link>
            <Link
              href="/dnpa-code-of-ethics"
              color="inherit"
              sx={{ textDecoration: 'none' }}
              title={`${TITLE} : DNPA Code of Ethics`}
            >
              DNPA
            </Link>
            <Link
              href="/affiliate-disclosure"
              color="inherit"
              sx={{ textDecoration: 'none' }}
              title={`${TITLE} : Affiliate Disclosure`}
            >
              Affiliate
            </Link>
            <Link
              href="#"
              color="inherit"
              sx={{ textDecoration: 'none', cursor: 'pointer' }}
              title="Manage Cookie Preferences"
              onClick={(e) => {
                e.preventDefault()
                if (typeof window !== 'undefined' && (window as any).showCookiePreferences) {
                  ;(window as any).showCookiePreferences()
                }
              }}
            >
              Cookies
            </Link>
          </Box>

          {/* Social Links - Mobile */}
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', mb: 2 }}>
            {APP_FACEBOOK_PROFILE && (
              <Link
                href={APP_FACEBOOK_PROFILE}
                title={`Facebook : ${TITLE}`}
                target="_blank"
                rel="noopener"
                color="inherit"
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  textDecoration: 'none',
                  fontSize: '0.7rem',
                  '&:hover': { color: 'primary.contrastText' },
                }}
              >
                <FacebookIcon fontSize="small" />
                Facebook
              </Link>
            )}
            {APP_TWITTER_PROFILE && (
              <Link
                href={APP_TWITTER_PROFILE}
                title={`Twitter : ${TITLE}`}
                target="_blank"
                rel="noopener"
                color="inherit"
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  textDecoration: 'none',
                  fontSize: '0.7rem',
                  '&:hover': { color: 'primary.contrastText' },
                }}
              >
                <XIcon fontSize="small" />
                Twitter
              </Link>
            )}
          </Box>
        </Box>

        {/* Desktop Layout - Original Grid */}
        <Box
          sx={{
            display: { xs: 'none', lg: 'flex' },
            flexDirection: 'row',
            gap: 6,
          }}
        >
          {/* Left Section - Branding & Social */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 3,
              flex: '0 0 300px',
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
              <Image
                src={`/${APP_NAMESPACE}/images/logo.png`}
                alt="Logo"
                width={180}
                height={50}
                loading="lazy"
                title={TITLE}
                sizes="(max-width: 600px) 120px, (max-width: 960px) 144px, 144px"
                style={{ height: 'auto', width: 'auto' }}
                onLoad={() => setIsLoaded(true)}
              />
            </Box>

            <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
              {APP_FACEBOOK_PROFILE && (
                <Link
                  href={APP_FACEBOOK_PROFILE}
                  title={`Facebook : ${TITLE}`}
                  target="_blank"
                  rel="noopener"
                  color="inherit"
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    textDecoration: 'none',
                    fontSize: '0.9rem',
                    '&:hover': { transform: 'translateY(-4px)' },
                    transition: 'all 0.2s ease',
                  }}
                >
                  <FacebookIcon fontSize="small" />
                  Facebook
                </Link>
              )}
              {APP_TWITTER_PROFILE && (
                <Link
                  href={APP_TWITTER_PROFILE}
                  title={`Twitter : ${TITLE}`}
                  target="_blank"
                  rel="noopener"
                  color="inherit"
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    textDecoration: 'none',
                    fontSize: '0.9rem',
                    '&:hover': { transform: 'translateY(-4px)' },
                    transition: 'all 0.2s ease',
                  }}
                >
                  <XIcon fontSize="small" />
                  Twitter
                </Link>
              )}
            </Box>
          </Box>

          {/* Right Section - Footer Links Grid */}
          <Box
            sx={{
              flex: 1,
              display: 'grid',
              gridTemplateColumns: 'repeat(4, 1fr)',
              gap: 4,
            }}
          >
            {/* Company Info */}
            <Box>
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 'bold',
                  mb: 2,
                }}
              >
                Company
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <Link
                  href="/about"
                  title={`${TITLE} : About Us`}
                  color="inherit"
                  sx={{
                    textDecoration: 'none',
                    fontSize: '0.875rem',
                    '&:hover': { transform: 'translateY(-4px)' },
                    transition: 'all 0.2s ease',
                  }}
                >
                  About Us
                </Link>
                <Link
                  href="/contact"
                  title={`${TITLE} : Contact Us`}
                  color="inherit"
                  sx={{
                    textDecoration: 'none',
                    fontSize: '0.875rem',
                    '&:hover': { transform: 'translateY(-4px)' },
                    transition: 'all 0.2s ease',
                  }}
                >
                  Contact
                </Link>
              </Box>
            </Box>

            {/* Legal & Policies */}
            <Box>
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 'bold',
                  mb: 2,
                }}
              >
                Legal
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <Link
                  href="/terms-of-service"
                  title={`${TITLE} : Terms of Service`}
                  color="inherit"
                  sx={{
                    textDecoration: 'none',
                    fontSize: '0.875rem',
                    '&:hover': { transform: 'translateY(-4px)' },
                    transition: 'all 0.2s ease',
                  }}
                >
                  Terms of Service
                </Link>
                <Link
                  href="/privacy-policy"
                  title={`${TITLE} : Privacy Policy`}
                  color="inherit"
                  sx={{
                    textDecoration: 'none',
                    fontSize: '0.875rem',
                    '&:hover': { transform: 'translateY(-4px)' },
                    transition: 'all 0.2s ease',
                  }}
                >
                  Privacy Policy
                </Link>
                <Link
                  href="/disclaimer"
                  title={`${TITLE} : Disclaimer`}
                  color="inherit"
                  sx={{
                    textDecoration: 'none',
                    fontSize: '0.875rem',
                    '&:hover': { transform: 'translateY(-4px)' },
                    transition: 'all 0.2s ease',
                  }}
                >
                  Disclaimer
                </Link>
              </Box>
            </Box>

            {/* Editorial Standards */}
            <Box>
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 'bold',
                  mb: 2,
                }}
              >
                Editorial
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <Link
                  href="/correction-policy"
                  title={`${TITLE} : Correction Policy`}
                  color="inherit"
                  sx={{
                    textDecoration: 'none',
                    fontSize: '0.875rem',
                    '&:hover': { transform: 'translateY(-4px)' },
                    transition: 'all 0.2s ease',
                  }}
                >
                  Correction Policy
                </Link>
                <Link
                  href="/fact-checking-policy"
                  title={`${TITLE} : Fact Checking Policy`}
                  color="inherit"
                  sx={{
                    textDecoration: 'none',
                    fontSize: '0.875rem',
                    '&:hover': { transform: 'translateY(-4px)' },
                    transition: 'all 0.2s ease',
                  }}
                >
                  Fact Checking
                </Link>
                <Link
                  href="/dnpa-code-of-ethics"
                  title={`${TITLE} : DNPA Code of Ethics`}
                  color="inherit"
                  sx={{
                    textDecoration: 'none',
                    fontSize: '0.875rem',
                    '&:hover': { transform: 'translateY(-4px)' },
                    transition: 'all 0.2s ease',
                  }}
                >
                  Ethics Code
                </Link>
              </Box>
            </Box>

            {/* Additional Links */}
            <Box>
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 'bold',
                  mb: 2,
                }}
              >
                More
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <Link
                  href="/affiliate-disclosure"
                  title={`${TITLE} : Affiliate Disclosure`}
                  color="inherit"
                  sx={{
                    textDecoration: 'none',
                    fontSize: '0.875rem',
                    '&:hover': { transform: 'translateY(-4px)' },
                    transition: 'all 0.2s ease',
                  }}
                >
                  Affiliate Disclosure
                </Link>
                <Link
                  href="#"
                  title="Manage Cookie Preferences"
                  color="inherit"
                  onClick={(e) => {
                    e.preventDefault()
                    if (typeof window !== 'undefined' && (window as any).showCookiePreferences) {
                      ;(window as any).showCookiePreferences()
                    }
                  }}
                  sx={{
                    textDecoration: 'none',
                    fontSize: '0.875rem',
                    '&:hover': { transform: 'translateY(-4px)' },
                    transition: 'all 0.2s ease',
                    cursor: 'pointer',
                  }}
                >
                  Manage Cookies
                </Link>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Copyright Section */}
      <Box
        sx={{
          borderTop: '1px solid rgba(255, 255, 255, 0.1)',
          mt: { xs: 2, md: 6 }, // Further reduced mobile margin
          pt: { xs: 1.5, md: 3 }, // Further reduced mobile padding
          textAlign: 'center',
          maxWidth: '1200px',
          mx: 'auto',
        }}
      >
        <Typography variant="caption" sx={{ opacity: 0.8, fontSize: { xs: '0.65rem', md: '0.75rem' } }}>
          &copy; {new Date().getFullYear()} {TITLE}. All rights reserved.
        </Typography>
      </Box>
    </Box>
  )
}

export default Footer
